import { useAuth } from "../contexts/AuthContext.jsx";
import { useQuery, useMutation } from "@tanstack/react-query";

// Wrapper for useQuery
export const useFetch = (key, url, options = {}) => {
  const { fetchWithAuth } = useAuth();

  return useQuery({
    queryKey: key,
    queryFn: async () => {
      const response = await fetchWithAuth(url);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(
          errorData.detail || `Request failed: ${response.statusText}`
        );
        error.response = errorData;
        error.status = response.status;
        throw error;
      }
      return response.json();
    },
    // A 4xx answers the same on every try (unknown id, no access): retrying it only delayed the
    // error by ~10 s. Network and server errors keep react-query's 3 retries.
    retry: (failureCount, error) => !(error.status >= 400 && error.status < 500) && failureCount < 3,
    ...options,
  });
};

// Wrapper for useMutation (Generic POST/PUT example)
export const useFetchMutation = () => {
  const { fetchWithAuth } = useAuth();

  return useMutation({
    mutationFn: async ({ url, method = "POST", headers = {}, body }) => {
      // Check if body is a plain JS object (and not FormData, Blob, etc.)
      if (
        body &&
        typeof body === "object" &&
        !(body instanceof FormData) &&
        !(body instanceof Blob) &&
        !(body instanceof URLSearchParams)
      ) {
        body = JSON.stringify(body);

        // Auto-add Content-Type if not present
        if (!headers["Content-Type"]) {
          headers["Content-Type"] = "application/json";
        }
      }

      const response = await fetchWithAuth(url, {
        method,
        headers,
        body,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(
          errorData.detail ||
            errorData.message ||
            `Mutation failed: ${response.statusText}`
        );
        error.response = errorData;
        error.status = response.status;
        throw error;
      }
      return response.json();
    },
  });
};
